import { createClient } from '@supabase/supabase-js'
import { env } from './env'

// Log all environment variables (except sensitive ones)
console.log('Environment variables loaded:', {
  VITE_API_URL: env.VITE_API_URL,
  VITE_SUPABASE_URL: env.VITE_SUPABASE_URL,
  // Don't log ANON_KEY
})

// In development, use the proxy to avoid CORS issues
const supabaseUrl = import.meta.env.DEV
  ? window.location.origin // Use the current origin to leverage the Vite proxy
  : env.VITE_SUPABASE_URL
const supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables:', {
    hasUrl: !!supabaseUrl,
    hasAnonKey: !!supabaseAnonKey
  })
  throw new Error('Missing Supabase environment variables')
}

// Ensure we're using the correct URL format
const normalizedSupabaseUrl = supabaseUrl.endsWith('/')
  ? supabaseUrl.slice(0, -1)  // Remove trailing slash if present
  : supabaseUrl

// Log the configuration in development
if (import.meta.env.DEV) {
  console.log('Initializing Supabase client:', {
    url: normalizedSupabaseUrl,
    mode: import.meta.env.MODE,
    isDev: import.meta.env.DEV,
    isProd: import.meta.env.PROD
  })
}

export const supabase = createClient(normalizedSupabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storageKey: 'sri-club-auth',
    flowType: 'pkce'
  },
  global: {
    fetch: (...args) => {
      // Use a custom fetch function to handle CORS issues
      const [url, options] = args;
      const modifiedOptions = {
        ...options,
        mode: 'cors' as RequestMode,
        credentials: 'include' as RequestCredentials,
        headers: {
          ...options?.headers,
          'Origin': window.location.origin,
        }
      };
      console.log('Supabase fetch:', { url, options: modifiedOptions });
      return fetch(url, modifiedOptions);
    }
  }
})

// TOTP verification helper methods
export const totpHelpers = {
  async getTOTPFactor() {
    const { data: factors, error } = await supabase.auth.mfa.listFactors()
    if (error) throw error
    if (!factors?.totp?.length) throw new Error('No TOTP factors found')
    return factors.totp[0]
  },

  async verifyTOTP(factorId: string, code: string) {
    // Get challenge
    const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId
    })
    if (challengeError) throw challengeError

    // Verify code
    const { data, error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.id,
      code
    })
    if (verifyError) throw verifyError

    return data
  },

  async createAndSetSession(data: any) {
    const session = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_in: data.expires_in,
      expires_at: Math.floor(Date.now() / 1000) + data.expires_in,
      token_type: data.token_type,
      user: data.user
    }
    
    return await supabase.auth.setSession(session)
  }
}